import {MediaObjectInterface} from '../../../core/interfaces/media-object.interface';
import {MediaObjectApiModel} from '../../../api/models/media-object.api-model';
import {ImageCropState} from '../../image-cropper/image-crop-state';

export interface MediaSelectionEntry {
  id: number;
  source?: MediaObjectInterface;
  sourceFile?: File;
  file?: File;
  uploaded?: MediaObjectApiModel;
  preview?: string;
  cropState?: ImageCropState;
}

export interface PreparedMediaSelection {
  media: MediaObjectInterface[];
  additions: {entryId: number; media: MediaObjectApiModel}[];
  replacements: {source: MediaObjectInterface; media: MediaObjectApiModel}[];
}

/** Form-local identity and order. File names are display labels, never keys. */
export class MediaSelection {
  entries: MediaSelectionEntry[] = [];
  private nextId = 0;

  addFile(file: File): MediaSelectionEntry {
    const entry = {id: ++this.nextId, sourceFile: file, file};
    this.entries.push(entry);
    return entry;
  }

  selectSources(sources: (MediaObjectInterface | string)[]): void {
    const media: MediaObjectInterface[] = sources.map(source => typeof source === 'string'
      ? {'@id': source, mimeType: '', dimensions: [], hyperlinks: []} : source);
    const ids = new Set(media.map(item => item['@id']));
    this.entries = this.entries.filter(entry => !entry.source || ids.has(entry.source['@id']));
    for (const source of media) {
      if (!this.entries.some(entry => entry.source?.['@id'] === source['@id'])) {
        this.entries.push({id: ++this.nextId, source});
      }
    }
  }

  crop(entry: MediaSelectionEntry, file: File, source: File, state?: ImageCropState): void {
    entry.sourceFile = source;
    entry.file = file;
    entry.uploaded = undefined;
    entry.cropState = state;
  }

  remove(entry: MediaSelectionEntry): void {
    this.entries = this.entries.filter(item => item.id !== entry.id);
  }

  prepared(): PreparedMediaSelection {
    if (this.entries.some(entry => entry.file && !entry.uploaded)) {
      throw new Error('Media selection still contains files awaiting upload');
    }
    return {
      media: this.entries.map(entry => entry.uploaded || entry.source!),
      additions: this.entries.filter(entry => !entry.source && entry.uploaded)
        .map(entry => ({entryId: entry.id, media: entry.uploaded!})),
      replacements: this.entries.filter(entry => entry.source && entry.uploaded)
        .map(entry => ({source: entry.source!, media: entry.uploaded!})),
    };
  }
}
